'use client';

import { c as _c } from "react/compiler-runtime";
import { useSlug } from '@cc-shopping/hooks/useSlug';
import { transformOfferWithExperiments } from '@cc-shopping/platform-absmartly/transforms';
import type { ExperimentState, TransformContext } from '@cc-shopping/platform-absmartly/transforms/types';
import { useAnalyticsSkuViewedContext } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed/Context';
import { useAnonymousId } from '@cc-shopping/platform-analytics/SegmentAnalyticsProvider';
import { theme } from '@cc-shopping/ui/theme';
import { datadogRum } from '@datadog/browser-rum';
import { useMediaQuery } from '@mui/material';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useExperimentOfferContext } from 'src/shared/providers/ExperimentOfferContext';
const TRANSFORMABLE_LINK_TYPES = new Set(['APPLY_NOW_BUTTON', 'OFFER_NAME', 'PRODUCT_IMAGE']);
const isRedirectLink = (url: string) => {
  try {
    return new URL(url).pathname.includes('/redirect/');
  } catch {
    return false;
  }
};
export function useExperimentalApplyLink(href, linkType, isReview) {
  const $ = _c(21);
  const offerData = useExperimentOfferContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = theme.breakpoints.down("md");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isMobile = useMediaQuery(t0);
  const slug = useSlug();
  const anonymousId = useAnonymousId();
  const pathname = usePathname();
  const {
    skuViewedPayload
  } = useAnalyticsSkuViewedContext();
  const origin = typeof window !== "undefined" ? window.location.origin : undefined;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const experiments = t1;
  const [transformedHref, setTransformedHref] = useState(href);
  const loggedRef = useRef(null);
  let t2;
  if ($[2] !== anonymousId || $[3] !== href || $[4] !== isMobile || $[5] !== isReview || $[6] !== linkType || $[7] !== offerData || $[8] !== pathname || $[9] !== skuViewedPayload || $[10] !== slug) {
    t2 = () => {
      if (!offerData || isReview || !TRANSFORMABLE_LINK_TYPES.has(linkType ?? "")) {
        setTransformedHref(href);
        return;
      }
      const logKey = `${href}|${offerData.offerId}|${linkType}`;
      if (!isRedirectLink(href)) {
        if (loggedRef.current !== logKey) {
          loggedRef.current = logKey;
          datadogRum.addError(new Error("Experiment transform blocked: href is not a redirect link"), {
            href,
            contextOfferId: offerData.offerId,
            linkType
          });
        }
        setTransformedHref(href);
        return;
      }
      const hrefMatchesOffer = href.includes(offerData.offerId);
      if (!hrefMatchesOffer && offerData.directApplyLink) {
        if (loggedRef.current !== logKey) {
          loggedRef.current = logKey;
          datadogRum.addError(new Error("Chase DTA transform blocked: href offer ID does not match context offer ID"), {
            href,
            contextOfferId: offerData.offerId,
            linkType
          });
        }
      }
      let cancelled = false;
      const transformableOffer = {
        id: offerData.offerId,
        details: {
          applyLink: href,
          directApplyLink: hrefMatchesOffer ? offerData.directApplyLink : undefined,
          isMonetizing: offerData.isMonetizing
        },
        institution: offerData.institution,
        product: offerData.product
      };
      const context = {
        isMobile,
        pathname,
        slug,
        anonymousId: anonymousId ?? undefined,
        skuViewedPayload: {
          ...skuViewedPayload,
          id: offerData.offerId,
          offer_id: offerData.offerId,
          monetizable: offerData.isMonetizing
        },
        origin
      };
      (async () => {
        const result = await transformOfferWithExperiments(transformableOffer, experiments, context);
        if (!cancelled) {
          setTransformedHref(result.details.applyLink);
        }
      })();
      return () => {
        cancelled = true;
      };
    };
    $[2] = anonymousId;
    $[3] = href;
    $[4] = isMobile;
    $[5] = isReview;
    $[6] = linkType;
    $[7] = offerData;
    $[8] = pathname;
    $[9] = skuViewedPayload;
    $[10] = slug;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== anonymousId || $[13] !== href || $[14] !== isMobile || $[15] !== isReview || $[16] !== linkType || $[17] !== offerData || $[18] !== skuViewedPayload || $[19] !== slug) {
    t3 = [href, offerData, experiments, isMobile, slug, anonymousId, origin, skuViewedPayload, linkType, isReview];
    $[12] = anonymousId;
    $[13] = href;
    $[14] = isMobile;
    $[15] = isReview;
    $[16] = linkType;
    $[17] = offerData;
    $[18] = skuViewedPayload;
    $[19] = slug;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  useEffect(t2, t3);
  return transformedHref;
}